import { useEffect, useRef } from "react";

const DRAG_THRESHOLD = 10; // pixels

function useDragger(id: string, type: string): void {

    let modal: HTMLElement | null | undefined = null;
    const isClicked = useRef<boolean>(false);
    const isDraggingRef = useRef<boolean>(false); // New ref to track if a drag is in progress
    const initialTouchCoords = useRef<{ clientX: number, clientY: number } | null>(null); // Store initial touch position

    const coords = useRef<{
        startX: number,
        startY: number,
        lastX: number,
        lastY: number
    }>({
        startX: 0,
        startY: 0,
        lastX: 0,
        lastY: 0
    })

    //makes component draggable
    useEffect(() => {
        const target = document.getElementById(id);
        let modalElement: HTMLElement | null | undefined;

        if (!target) {
             console.error("Element with given id doesnt exist");
             return;
        }

        if (type == 'AppointmentInfoModal') {
            modalElement = target.parentElement?.parentElement;
        } else {
            modalElement = target.parentElement;
        }

        if (!modalElement) {
             console.error('Modal doesnt exist');
             return;
        }

        modal = modalElement;

        const container = target.closest('html');
        if (!container) {
             console.error("Target element must have a parent leading to html");
             return;
        }

        // Set initial position in coords
        coords.current.lastX = modal.offsetLeft;
        coords.current.lastY = modal.offsetTop;

        const updateModalPosition = (clientX: number, clientY: number) => {
            const nextX = clientX - coords.current.startX + coords.current.lastX;
            const nextY = clientY - coords.current.startY + coords.current.lastY;

            modal!.style.top = `${nextY}px`;
            modal!.style.left = `${nextX}px`;
        };

        // --- Mouse Event Handlers ---
        const onMouseDown = (e: MouseEvent) => {
            isClicked.current = true;
            coords.current.startX = e.clientX;
            coords.current.startY = e.clientY;
            isDraggingRef.current = false; // Reset dragging flag for mouse
        };

        const onMouseUp = () => {
            isClicked.current = false;
            if (modal) {
                 coords.current.lastX = modal.offsetLeft;
                 coords.current.lastY = modal.offsetTop;
            }
             isDraggingRef.current = false; // Reset dragging flag
        };

        const onMouseMove = (e: MouseEvent) => {
            if (!isClicked.current) return;
             // For mouse, we consider any movement while clicked as dragging
             isDraggingRef.current = true;
            updateModalPosition(e.clientX, e.clientY);
        };

        // --- Touch Event Handlers ---
        const onTouchStart = (e: TouchEvent) => {
            // Do NOT prevent default here initially
            isClicked.current = true;
            isDraggingRef.current = false; // Assume it's not a drag yet
            const touch = e.touches[0];
            coords.current.startX = touch.clientX;
            coords.current.startY = touch.clientY;
            initialTouchCoords.current = { clientX: touch.clientX, clientY: touch.clientY }; // Store initial touch position
        };

        const onTouchEnd = () => {
            isClicked.current = false;
             if (modal) {
                 coords.current.lastX = modal.offsetLeft;
                 coords.current.lastY = modal.offsetTop;
             }
            initialTouchCoords.current = null; // Clear initial touch position

            // isDraggingRef is checked in onTouchMove.
            // If isDraggingRef.current is false, it means it was a tap, and preventDefault was not called in touchmove, allowing the click.
            // If isDraggingRef.current is true, preventDefault was called in touchmove, preventing the click.
             isDraggingRef.current = false; // Reset dragging flag
        };

        const onTouchMove = (e: TouchEvent) => {
            if (!isClicked.current || !initialTouchCoords.current) return;

            const touch = e.touches[0];
            const currentClientX = touch.clientX;
            const currentClientY = touch.clientY;

            const distance = Math.sqrt(
                (currentClientX - initialTouchCoords.current.clientX)**2 +
                (currentClientY - initialTouchCoords.current.clientY)**2
            );

            if (distance > DRAG_THRESHOLD) {
                isDraggingRef.current = true; // It's a drag
                e.preventDefault(); // Prevent default only when dragging
                updateModalPosition(currentClientX, currentClientY);
            } else {
                 // If movement is within threshold, do not call preventDefault,
                 // and do not update position yet.
                 // The browser might still fire a click if the touch ends without significant movement.
            }
        };


        // --- Add Event Listeners ---
        // Mouse Listeners
        target.addEventListener('mousedown', onMouseDown);
        target.addEventListener('mouseup', onMouseUp);
        container.addEventListener('mousemove', onMouseMove);
        container.addEventListener('mouseleave', onMouseUp);

        // Touch Listeners
        target.addEventListener('touchstart', onTouchStart);
        // Listen for touchend on target and container
        target.addEventListener('touchend', onTouchEnd);
        container.addEventListener('touchmove', onTouchMove);
        container.addEventListener('touchend', onTouchEnd);
        // container.addEventListener('touchcancel', onTouchEnd); // Optional

        // --- Cleanup function ---
        const cleanup = () => {
            // Mouse Listeners
            target.removeEventListener('mousedown', onMouseDown);
            target.removeEventListener('mouseup', onMouseUp);
            container.removeEventListener('mousemove', onMouseMove);
            container.removeEventListener('mouseleave', onMouseUp);

            // Touch Listeners
            target.removeEventListener('touchstart', onTouchStart);
            target.removeEventListener('touchend', onTouchEnd);
            container.removeEventListener('touchmove', onTouchMove);
            container.removeEventListener('touchend', onTouchEnd);
            // if added: container.removeEventListener('touchcancel', onTouchEnd);
        };

        return cleanup;
    }, [id, type]);

}

export default useDragger;